import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Copy, Trash2, Printer, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Header } from "@/components/Header";
import { WeeklyPlanDisplay } from "@/components/KindDisplay";
import { Button } from "@/components/ui/button";

export default function WeeklyPlanView() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/weekly-plans/${id}`).then(({ data }) => setPlan(data)).catch(() => toast.error("Plan not found")).finally(() => setLoading(false));
  }, [id]);

  const duplicate = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/weekly-plans/${id}/duplicate`); toast.success(t("common.duplicate") + " ✓"); navigate(`/weekly-plans/${data.id}`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const remove = async () => {
    try { await api.delete(`/weekly-plans/${id}`); toast.success(t("common.delete") + " ✓"); navigate("/weekly-plans"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (loading) return (<div className="min-h-screen bg-background"><Header /><div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></div>);
  if (!plan) return null;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="flex items-center justify-between gap-3 no-print">
          <button onClick={() => navigate("/weekly-plans")} className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm font-semibold" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4" /> {lang === "es" ? "Mis Planes" : "My Plans"}
          </button>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" className="rounded-full" onClick={duplicate} disabled={busy} data-testid="plan-duplicate"><Copy className="h-4 w-4" /> {t("common.duplicate")}</Button>
            <Button variant="outline" size="sm" className="rounded-full" onClick={() => window.print()} data-testid="plan-print"><Printer className="h-4 w-4" /> {t("common.print")}</Button>
            <Button variant="outline" size="sm" className="rounded-full text-destructive border-destructive/30" onClick={remove} data-testid="plan-delete"><Trash2 className="h-4 w-4" /> {t("common.delete")}</Button>
          </div>
        </div>
        <div className="rounded-3xl border border-border bg-card p-6 sm:p-10">
          <WeeklyPlanDisplay content={plan.content} t={t} />
        </div>
      </main>
    </div>
  );
}
